import { FC, Fragment, useEffect } from 'react';
import { useFormContext, useFieldArray } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useToggle from 'app/hooks/useToggle';
// Redux
import { useAppDispatch } from 'app/hooks/useStore';
// Models
import IContact from 'app/models/Contact';
// Async
import { getContactsAutocomplete } from 'app/store/Contacts/Contacts.async';
// Actions
import { ContactsActions } from 'app/store/Contacts/Contacts.slice';
// Icons
import { Add as AddIcon } from '@mui/icons-material';
// Components
import { Button } from 'app/components/Mui';
import CaseContactItem from './CaseContactItem';
import ContactsFormDialog from 'app/pages/Admin/ContactsPage/ContactsFormDialog';

interface IFormData {
  contacts: {
    contact: IContact | null;
    role: string;
  }[];
}

type Props = {
  disabled?: boolean;
  onSetError?: (hasError: boolean) => void;
}

const CaseContacts:FC<Props> = ({ disabled = false, onSetError }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const { control, formState: { errors, isSubmitted } } = useFormContext<IFormData>();

  useEffect(() => {
    if ( !isSubmitted || !onSetError ) return;
    onSetError(Boolean(errors.contacts));
  }, [isSubmitted, errors.contacts, onSetError]);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  })

  const onAddContact = (contact?:IContact) => {
    append({ contact: contact || null, role: '' });
  }

  useEffect(() => {
    dispatch(getContactsAutocomplete({ limit: 50 }));
    return () => {
      dispatch(ContactsActions.setInitialField('autocomplete'));
    }
    // eslint-disable-next-line
  }, []);

  const { open, toggle } = useToggle();

  const handleCloseContactsForm = () => {
    dispatch(getContactsAutocomplete({ limit: 50 }));
    toggle();
  }

  return (
    <Fragment>
      <Button
        name="Create new contact"
        variant="contained"
        size="small"
        onClick={toggle}
      >{t('components.caseContacts.createNewContact')}</Button>
      {fields.map((field, index) => (
        <CaseContactItem key={field.id} index={index} onRemove={remove} />
      ))}
      <Button
        name="Assign contact to case"
        size="small"
        onClick={() => onAddContact()}
        disabled={disabled}
        startIcon={<AddIcon />}
        sx={{ mt: 2, display: 'flex' }}
      >{t('components.caseContacts.linkContact')}</Button>

      {open ? (
        <ContactsFormDialog
          open={open}
          onClose={handleCloseContactsForm}
          onCreated={onAddContact}
        />
      ) : null}
    </Fragment>
  );
};

export default CaseContacts;
